import { useCallback, useMemo, useRef, useState } from 'react';

export interface AsyncResource<T> {
  data: T | undefined;
  loading: boolean;
  error: string | undefined;
  /** Fetch once if not already loaded or in flight; idempotent, so it's safe to call on every mount. */
  ensureLoaded: () => void;
  /** Force a refetch, bypassing the cache. */
  refresh: () => void;
}

/**
 * A lazily-loaded, cached async resource. The first `ensureLoaded()` triggers the fetch;
 * later calls are no-ops while a result is cached or a request is already in flight (request
 * de-duplication). `refresh()` forces a refetch. The state lives with the caller — a provider
 * mounted above the view switch — so it survives as long as that provider is mounted, which is
 * how navigating between StarGrid sub-views avoids re-hitting the API.
 *
 * `ensureLoaded` / `refresh` keep a stable identity across renders, and the returned object is
 * memoized on its own state, so consumers only re-render when this resource actually changes.
 */
export function useAsyncResource<T>(fetcher: () => Promise<T>): AsyncResource<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);

  // Transient control flags that must not trigger re-renders.
  const loadedRef = useRef<boolean>(false);
  const inFlightRef = useRef<boolean>(false);
  // Keep the latest fetcher without changing `load`'s identity (useLatest pattern).
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const load = useCallback(async () => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    setLoading(true);
    setError(undefined);
    try {
      const result = await fetcherRef.current();
      setData(result);
      loadedRef.current = true;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unknown error');
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  }, []);

  const ensureLoaded = useCallback(() => {
    if (!loadedRef.current && !inFlightRef.current) {
      void load();
    }
  }, [load]);

  const refresh = useCallback(() => {
    loadedRef.current = false;
    void load();
  }, [load]);

  return useMemo(() => ({ data, loading, error, ensureLoaded, refresh }), [data, loading, error, ensureLoaded, refresh]);
}

/**
 * Derived "is this resource still pending" flag: true until the first load resolves with either
 * data or an error, and while a refetch is in flight. Lets views show a spinner on first paint
 * (before `ensureLoaded`'s effect has run) without a flash of the empty state.
 */
export function isResourcePending<T>(resource: AsyncResource<T>): boolean {
  return resource.loading || (resource.data === undefined && resource.error === undefined);
}
